import React from 'react'
import { connect } from 'react-redux'
import { NavLink, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'
import {
  AppBar,
  Button,
  Container,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SubcontractorDashboardV2 from './SubcontractorDashboardV2'
import SubcontractorSchedule from './SubcontractorSchedule'
import SubcontractorJobsites from './SubcontractorJobsites'
import SubcontractorCrews from './SubcontractorCrews'
import SubcontractorTerritories from './SubcontractorTerritories'
import SubcontractorUsers from './SubcontractorUsers'
import SubcontractorBlends from './SubcontractorBlends'
import { derivePortalTheme } from './portalShared'
import SubcontractorSettings from './SubcontractorSettings'
import SubcontractorMessages from './SubcontractorMessages'
import SubcontractorNotifications from './SubcontractorNotifications'
import SubcontractorWaterAccess from './SubcontractorWaterAccess'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'var(--df-bg)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 48,
    maxHeight: 48
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  tabBarInner: {
    maxWidth: theme.breakpoints.values.lg,
    margin: '0 auto',
    width: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },
  tabs: {
    minHeight: 42
  },
  shell: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(5)
  },
  sectionWrap: {
    marginTop: theme.spacing(1)
  }
}))

const navItems = [
  {
    label: 'Dashboard',
    slug: '',
    description: 'Your command center — field activity, upcoming work, team status, and office messages.'
  },
  {
    label: 'Schedule',
    slug: '/schedule',
    description: 'View and filter upcoming herbicide work across your crews.'
  },
  {
    label: 'Jobsites',
    slug: '/jobsites',
    description: 'Browse your herbicide properties in list or map view.'
  },
  {
    label: 'Crews',
    slug: '/crews',
    description: 'Create crews, assign team members, and manage your operating groups.'
  },
  {
    label: 'Territories',
    slug: '/territories',
    description: 'Draw territory boundaries and assign them to your crews.'
  },
  {
    label: 'Blends',
    slug: '/blends',
    description: 'Build and manage your herbicide blend recipes.'
  },
  {
    label: 'Water Access',
    slug: '/water-access',
    description: 'Find and manage water refill locations.'
  },
  {
    label: 'Users',
    slug: '/users',
    description: 'See who has portal access and manage your team.'
  },
  {
    label: 'Messages',
    slug: '/messages',
    description: 'View conversations and reply to messages.'
  },
  {
    label: 'Notifications',
    slug: '/notifications',
    description: 'Choose how you get notified about new messages.'
  },
  {
    label: 'Settings',
    slug: '/settings'
  }
]

function getActiveTabIndex (pathname, basePath, items) {
  if (pathname === basePath) return 0
  const idx = items.findIndex(item => item.slug && pathname.startsWith(`${basePath}${item.slug}`))
  return idx >= 0 ? idx : 0
}

const SubcontractorPortalApp = ({ user }) => {
  const classes = useStyles()
  const location = useLocation()
  const { path, url } = useRouteMatch()
  const profile = user?.subcontractorProfile || {}
  const portalBranding = profile.portalBranding || {}
  const theme = derivePortalTheme(portalBranding.primaryColor)
  const headerDisplayName = portalBranding.displayName || profile.vendorName || 'Spray Contractor Workspace'
  const visibleNavItems = navItems.filter((item) => {
    if (item.slug === '/crews') {
      return profile?.capabilities?.canManageCrews === true
    }
    if (item.slug === '/messages') {
      return profile?.capabilities?.canViewMessagesV2 === true
    }
    if (item.slug === '/notifications') {
      return profile?.capabilities?.canViewNotificationPrefs === true
    }
    if (item.slug === '/settings') return profile?.role === 'admin'

    return true
  })
  const activeTabIndex = getActiveTabIndex(location.pathname, path, visibleNavItems)

  const roleBadge = (profile.role || 'applicator').charAt(0).toUpperCase() + (profile.role || 'applicator').slice(1)
  const crewScope = profile.crewName ? `Crew ${profile.crewName}` : 'All crews'

  return (
    <div className={`${classes.root} desert-flora`}>
      <AppBar position="static" elevation={0} style={{ background: theme.primary, height: 48 }}>
        <Toolbar className={classes.toolbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {portalBranding.logoUrl ? (
              <img
                src={portalBranding.logoUrl}
                alt=""
                style={{ height: 28, objectFit: 'contain', borderRadius: 4 }}
              />
            ) : null}
            <Typography style={{ fontWeight: 700, color: theme.textOnPrimary, fontSize: 15, letterSpacing: '-0.01em' }}>
              {headerDisplayName}
            </Typography>
          </div>

          <div className={classes.toolbarRight}>
            <Typography style={{ color: theme.textOnPrimaryMuted, fontSize: 12.5 }}>
              {roleBadge} &middot; {crewScope}
            </Typography>
            <Button href="/logout" variant="outlined" size="small" style={{ color: theme.textOnPrimary, borderColor: 'rgba(251,248,241,.30)', backgroundColor: 'transparent', fontSize: 12, padding: '4px 10px' }}>
              Log out
            </Button>
          </div>
        </Toolbar>
      </AppBar>

      <div style={{ borderBottom: '1px solid var(--df-line)', background: 'transparent' }}>
        <div className={classes.tabBarInner}>
          <Tabs
            value={activeTabIndex}
            classes={{ root: classes.tabs }}
            TabIndicatorProps={{ style: { backgroundColor: 'var(--df-primary)', height: 2 } }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {visibleNavItems.map((item) => (
              <Tab
                key={item.label}
                label={item.label}
                component={NavLink}
                exact={item.slug === ''}
                to={`${url}${item.slug}`}
                style={{ color: 'var(--df-ink-3)', minHeight: 42, fontSize: 13.5, fontWeight: 500, minWidth: 'auto', padding: '14px 14px 12px' }}
              />
            ))}
          </Tabs>
        </div>
      </div>

      <Container maxWidth="lg" className={classes.shell}>
        <div className={classes.sectionWrap}>
          <Switch>
            <Route path={path} exact component={SubcontractorDashboardV2} />
            <Route path={`${path}/schedule`} component={SubcontractorSchedule} />
            <Route path={`${path}/jobsites`} component={SubcontractorJobsites} />
            <Route path={`${path}/crews`} component={SubcontractorCrews} />
            <Route path={`${path}/territories`} component={SubcontractorTerritories} />
            <Route path={`${path}/blends`} component={SubcontractorBlends} />
            <Route path={`${path}/water-access`} component={SubcontractorWaterAccess} />
            <Route path={`${path}/users`} component={SubcontractorUsers} />
            <Route path={`${path}/messages/:threadId?`} component={SubcontractorMessages} />
            <Route path={`${path}/notifications`} component={SubcontractorNotifications} />
            <Route path={`${path}/settings`} component={SubcontractorSettings} />
            <Redirect to={path} />
          </Switch>
        </div>
      </Container>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorPortalApp)
